import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Lenis from 'lenis';
import axios from 'axios';
import { ArrowRight, Phone, MapPin, Envelope, Star, CheckCircle } from '@phosphor-icons/react';

const allReviews = [
  { name: 'Demetris Ioannou', text: 'Very professional, with good knowledge of their work. Printed our wedding invitations from them. Great work with many suggestions based on our wants.', rating: 5 },
  { name: 'Kyriakos Efstathiou', text: 'Very professional and with excellent customer service! We have printed our wedding invitations there and we are very satisfied! Highly recommended for everyone!', rating: 5 },
  { name: 'stelios eleftheriou', text: 'I personally chose the shop for my wedding invitations and the service was excellent, while the quality was truly amazing. The owner is kind, pleasant and very willing to help with anything needed. Highly recommended!', rating: 5 },
  { name: 'T Chrys', text: 'Friendly people and good work. No hassle, you talk it through and it\'s done.', rating: 5 },
  { name: '\u0395\u03A5\u03A6\u03A1\u039F\u03A3\u03A5\u039D\u0397 \u039A\u039F\u03A5\u03A4\u03A3\u039F\u03A5\u0392\u0395\u03A1\u0397', text: 'Perfect result, very good collaboration!', rating: 5 },
];

// Show 3 random reviews on each render
const shuffled = [...allReviews].sort(() => 0.5 - Math.random());
const reviews = shuffled.slice(0, 3);

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };

const HomePage = () => {
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.4, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true });
    lenisRef.current = lenis;
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/product-configs?active_only=true`)
      .then(({ data }) => setCategories(data.sort((a, b) => (a.order || 99) - (b.order || 99))))
      .catch(() => {});
  }, []);

  const descriptions = {
    'Business Cards': 'Premium finishes & professional quality',
    'Flyers': 'Eye-catching designs for every occasion',
    'Brochures': 'Polished multi-fold presentations',
    'Bookmarks': 'Custom designs with premium lamination',
    'Envelopes': 'Branded stationery that impresses',
    'Invitations (Wedding / Party)': 'Elegant designs on luxury paper',
    'Document Printing': 'Sharp, reliable document reproduction',
    'Notepads (Business)': 'Branded pads for daily operations',
    'Pads (General small print)': 'Compact pads for quick notes',
    'Custom Request': 'Bespoke solutions for any project',
  };

  const getSpan = (i) => {
    if (i === 0 || i === 5) return 'md:col-span-1 md:row-span-2';
    return 'md:col-span-1 md:row-span-1';
  };

  const fallbackImg = 'https://images.pexels.com/photos/6620989/pexels-photo-6620989.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  return (
    <div className="paper-texture-bg min-h-screen">
      {/* ─── HERO SECTION ─── */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
        {/* Clean paper background — subtle texture only */}
        <motion.div style={{ y }} className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[#F8F6F2]" />
          <img
 		 src="/hero-paper.webp"
  		alt=""
 		 width="1920"
 		 height="1080"
  		className="absolute inset-0 w-full h-full object-cover opacity-[0.18]"
  		loading="eager"
  		fetchpriority="high"
	/>        
  {/* Soft vignette for depth */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(240,237,232,0.5)_100%)]" />
        </motion.div>

        {/* Bottom fade into next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#F8F6F2] to-transparent z-[1]" />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-8 md:px-12 flex-1 flex flex-col">
          {/* Top bar — brand name + tagline left, est. right */}
          <div className="relative h-28 flex-shrink-0">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="absolute top-[26px] left-0">
              <h2 className="text-lg md:text-xl font-bold text-[#1a1410] tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
  A. Loizides & Sons LTD
</h2>
              <p className="text-[#C6A87A] text-[11px] tracking-[0.15em] mt-1.5 italic font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                "You name it… we print it."
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="absolute top-[30px] right-0">
              <p className="text-[#9B8E7E] text-xs tracking-[0.3em] uppercase font-medium">EST. 1970 — CYPRUS</p>
            </motion.div>
          </div>

          {/* Center Content */}
          <motion.div style={{ opacity: heroOpacity }} className="flex-1 flex items-center justify-center">
            <motion.div className="text-center max-w-5xl" variants={stagger} initial="hidden" animate="visible">
            <h1 className="sr-only">
             Professional Printing Services in Cyprus
            </h1>

<p className="sr-only">
  A. Loizides & Sons LTD offers premium printing services in Cyprus including business cards, flyers, brochures and more.
</p>
              <motion.h2 variants={fadeUp} className="mb-8" data-testid="hero-heading">
                <span className="block text-5xl md:text-7xl lg:text-[5.5rem] font-bold text-[#1a1410] tracking-tighter leading-none mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Premium Printing
                </span>
                <span className="block text-4xl md:text-6xl lg:text-7xl font-light tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', background: 'linear-gradient(135deg, #4A3F35 0%, #C6A87A 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Services in Cyprus
                </span>
              </motion.h2>

              <motion.p variants={fadeUp} className="text-base md:text-lg text-[#5A5046]/80 mb-10 leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hero-subtext">
                Business cards, flyers, brochures & more — fast delivery and exceptional quality for over 50 years.
              </motion.p>

              {/* Trust bullets */}
              <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-12">
                {['Premium Materials', 'Fast Turnaround', 'Trusted by Businesses in Cyprus'].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-[#5A5046]/50 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <CheckCircle size={16} weight="fill" className="text-[#C6A87A]" />
                    <span>{t}</span>
                  </div>
                ))}
              </motion.div>

              {/* CTAs */}
              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-5">
                <Link to="/quote" data-testid="hero-cta-button" className="group inline-flex items-center gap-3 bg-[#1a1410] hover:bg-[#2a2420] text-[#F8F6F2] px-10 md:px-12 py-5 md:py-6 text-base font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-[#1a1410]/10 hover:scale-[1.03] rounded-sm">
                  Get a Quote
                  <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href="tel:25366249" className="inline-flex items-center gap-3 border border-[#1a1410]/12 hover:border-[#C6A87A]/40 text-[#1a1410] px-10 md:px-12 py-5 md:py-6 text-base font-medium transition-all duration-300 hover:bg-[#1a1410]/[0.03] rounded-sm">
                  <Phone size={20} weight="bold" className="text-[#C6A87A]" />25366249
                </a>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5, delay: 1.5 }} className="pb-10">
            <div className="w-px h-14 bg-gradient-to-b from-[#C6A87A]/30 to-transparent mx-auto" />
          </motion.div>
        </div>
      </section>


{/* --- SEO CONTENT (CRITICAL FOR GOOGLE) --- */}
<section className="sr-only">
  <h1 className="text-3xl md:text-4xl font-bold text-[#1a1410] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
    Printing Services in Cyprus - A. Loizides & Sons LTD
  </h1>

  <p className="text-base md:text-lg text-[#4A3F35] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
    A. Loizides & Sons LTD offers professional offset and digital printing services in Cyprus,
    including business cards, flyers, brochures, booklets, posters, and custom print solutions.
  </p>

  <p className="text-base md:text-lg text-[#4A3F35] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
    With over 50 years of experience, we deliver high-quality printing with premium materials,
    fast turnaround times, and competitive pricing for businesses across Cyprus.
  </p>

  <p className="text-base md:text-lg text-[#4A3F35]" style={{ fontFamily: 'Outfit, sans-serif' }}>
    Request your custom quote today and work with one of the most trusted printing companies in Cyprus.
  </p>
</section>


      {/* --- SERVICES SECTION --- */}
<section className="relative py-28 md:py-36 px-6 md:px-12 lg:px-24 bg-[#F8F6F2]" data-testid="categories-section">
  <div className="max-w-7xl mx-auto relative z-10">
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-20">
      <p className="text-[#C6A87A] text-xs tracking-[0.25em] uppercase font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>What We Offer</p>
      <h2 className="text-3xl md:text-5xl font-bold text-[#1a1410] tracking-tight mb-5" style={{ fontFamily: 'Playfair Display, serif' }}>
        Our Services
      </h2>
      <p className="text-base md:text-lg text-[#4A3F35] max-w-xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Select a product to get a custom quote tailored to your needs
      </p>
    </motion.div>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-5 auto-rows-[200px]">
      {categories.map((cat, i) => (
        <Link
          key={cat.id}
          to={`/quote?product=${encodeURIComponent(cat.product)}`}
          data-testid={`category-${cat.product.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.04 }}
            className={`${getSpan(i)} relative group overflow-hidden cursor-pointer rounded-xl h-full`}
            style={{ boxShadow: '0 4px 30px rgba(26,20,16,0.12)' }}
          >
            {/* IMAGE */}
            <img
              src={cat.image || fallbackImg}
              alt={`${cat.product} printing Cyprus`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />

            {/* OVERLAY (improved) */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/90 transition-all duration-500" />

            {/* CONTENT */}
            <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-7 z-10">
              <h3
                className="text-lg md:text-xl font-semibold text-white mb-1 transition-transform duration-300 group-hover:translate-x-1"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {cat.product}
              </h3>

              <p
                className="text-xs text-white/70 mb-2 leading-relaxed"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {descriptions[cat.product] || `${cat.fields.length} customizable options`}
              </p>

              <div className="flex items-center gap-1.5 text-[#C6A87A] opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                <span
                  className="text-xs font-semibold tracking-wide"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Get Quote
                </span>
                <ArrowRight size={14} weight="bold" />
              </div>
            </div>
          </motion.div>
        </Link>
      ))}
    </div>
  </div>
</section>

      {/* ─── TRUST / LEGACY SECTION ─── */}
      <section className="relative py-28 md:py-36 px-6 md:px-12 lg:px-24 bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <p className="text-[#C6A87A] text-xs tracking-[0.25em] uppercase font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Legacy</p>
              <h2 className="text-3xl md:text-5xl font-bold text-[#1a1410] tracking-tight mb-8 leading-[1.1]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Over 50 Years of<br />Printing <span className="text-[#C6A87A]">Excellence</span>
              </h2>
              <p className="text-base md:text-lg text-[#5A5046]/70 leading-relaxed mb-14" style={{ fontFamily: 'Outfit, sans-serif' }}>
                A. Loizides & Sons LTD has been Cyprus's trusted printing partner for over five decades. Our commitment to exceptional quality and reliability has served thousands of businesses across the island.
              </p>

              <div className="space-y-8">
                {[
                  { title: 'Premium Quality', desc: 'State-of-the-art technology ensures every project meets the highest standards.' },
                  { title: 'Fast Turnaround', desc: 'Quick delivery without compromising quality. Your deadlines are our priority.' },
                  { title: 'Expert Support', desc: 'Personalized service and guidance from our experienced team.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-5 group">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 border border-[#C6A87A]/30 bg-[#C6A87A]/5 rounded-lg transition-all duration-300 group-hover:bg-[#C6A87A]/10 group-hover:border-[#C6A87A]/50">
                      <Star size={22} weight="fill" className="text-[#C6A87A]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#1a1410] mb-1.5" style={{ fontFamily: 'Playfair Display, serif' }}>{item.title}</h3>
                      <p className="text-sm text-[#4A3F35] leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="relative h-[480px] lg:h-[600px] group">
              <div className="absolute -inset-3 bg-gradient-to-br from-[#C6A87A]/15 to-transparent rounded-2xl transition-opacity duration-300 group-hover:opacity-50" />
              <img
                src="/images/digital-printing-cyprus.webp"
                alt="Printing Press"
                className="relative w-full h-full object-cover rounded-xl shadow-2xl transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── REVIEWS SECTION ─── */}
      <section className="relative py-28 md:py-36 px-6 md:px-12 lg:px-24 bg-[#F8F6F2]" data-testid="reviews-section">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-16">
            <div className="flex justify-center gap-1.5 mb-6">
              {[...Array(5)].map((_, i) => <Star key={i} size={26} weight="fill" className="text-[#C6A87A]" />)}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-[#1a1410] tracking-tight mb-5" style={{ fontFamily: 'Playfair Display, serif' }}>
              What Our Clients Say
            </h2>
            <p className="text-base text-[#5A5046]/50 max-w-lg mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Real feedback from businesses across Cyprus
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {reviews.map((review, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white border border-[#E8E4DE] rounded-xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#1a1410]/[0.04] hover:border-[#C6A87A]/30"
                data-testid={`review-card-${i}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, j) => <Star key={j} size={16} weight="fill" className="text-[#C6A87A]" />)}
                </div>
                <p className="text-sm text-[#5A5046] leading-relaxed mb-5 italic line-clamp-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  "{review.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#C6A87A]/10 flex items-center justify-center text-sm font-bold text-[#C6A87A] flex-shrink-0">
                    {review.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1a1410]">{review.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                      <span className="text-[10px] text-[#5A5046]/35 tracking-wide">Verified Google Review</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <a href="https://g.page/r/CeWObZ8n9aXoEBE/review" target="_blank" rel="noopener noreferrer" data-testid="leave-review-button" className="inline-flex items-center gap-3 bg-[#1a1410] hover:bg-[#2a2420] text-[#F8F6F2] px-9 py-4 text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-[#1a1410]/10 hover:scale-[1.03] rounded-sm">
              Leave a Review on Google
              <ArrowRight size={18} weight="bold" />
            </a>
          </div>
        </div>
      </section>

      {/* ─── CONTACT / MAP ─── */}
      <section className="relative py-28 md:py-36 px-6 md:px-12 lg:px-24" data-testid="contact-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <p className="text-[#C6A87A] text-xs tracking-[0.25em] uppercase font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Get In Touch</p>
              <h2 className="text-3xl md:text-5xl font-bold text-[#1a1410] tracking-tight mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>
                Visit Our Location
              </h2>
              <div className="space-y-8">
                {[
                  { Icon: MapPin, title: 'Location', content: 'Michael Pselou 5 Agios Athanasios,Limassol', href: null },
                  { Icon: Phone, title: 'Phone', content: '25366249', href: 'tel:25366249' },
                  { Icon: Envelope, title: 'Email', content: 'info@aloizidesltd.com', href: 'mailto:info@aloizidesltd.com' },
                ].map((c) => (
                  <div key={c.title} className="flex items-start gap-5 group">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-[#C6A87A]/8 border border-[#C6A87A]/20 rounded-lg transition-all duration-300 group-hover:bg-[#C6A87A]/15">
                      <c.Icon size={24} weight="fill" className="text-[#C6A87A]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#1a1410] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{c.title}</h3>
                      {c.href ? (
                        <a href={c.href} className="text-base text-[#4A3F35] hover:text-[#C6A87A] transition-colors">{c.content}</a>
                      ) : (
                        <p className="text-base text-[#4A3F35]">{c.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="h-[420px] lg:h-[520px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1630!2d33.0651686!3d34.7054973!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14e735be99f019c5%3A0xe8a5f5279f6d8ee5!2sA.Loizides%20%26%20Son&#39;s%20LTD!5e0!3m2!1sen!2scy!4v1"
                width="100%" height="100%"
                style={{ border: 0, borderRadius: '12px', boxShadow: '0 4px 30px rgba(26,20,16,0.1)' }}
                allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                title="A. Loizides &amp; Sons LTD - Google Maps" data-testid="google-maps-embed"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-[#0d0a07] text-[#F5F3ED] py-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>A. Loizides & Sons LTD</h3>
              <p className="text-[#F5F3ED]/40 text-sm leading-relaxed">Premium printing services in Cyprus for over 50 years.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4 uppercase tracking-wider text-[#C6A87A]/60">Quick Links</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/quote" className="text-[#F5F3ED]/50 hover:text-[#C6A87A] transition-colors">Get a Quote</Link></li>
                <li><a href="tel:25366249" className="text-[#F5F3ED]/50 hover:text-[#C6A87A] transition-colors">25366249</a></li>
                <li><a href="mailto:info@aloizidesltd.com" className="text-[#F5F3ED]/50 hover:text-[#C6A87A] transition-colors">info@aloizidesltd.com</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4 uppercase tracking-wider text-[#C6A87A]/60">Services</h4>
              <ul className="space-y-2.5 text-sm text-[#F5F3ED]/40">
                {categories.slice(0, 5).map((cat) => <li key={cat.id}>{cat.product}</li>)}
              </ul>
            </div>
          </div>
          <div className="border-t border-[#F5F3ED]/[0.06] pt-8">
            <p className="text-xs text-[#F5F3ED]/25 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              &copy; 2026 A. Loizides & Sons LTD. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
